import React from 'react';
import { formatPhoneNumber } from '@/lib/utils';
import { BILLING_TYPE, RECAP_FILE_TYPE, RECAP_FREQUENCY, STATEMENT_FILE_TYPE } from '@/utils/constant';
import {
  BillingPreferenceResponse,
  ClientGetByIdResponse,
  RecapFileType,
  RecapFrequency,
  StatementFileType,
} from '@/types/client.types';

// The API can return null for these optional settings.
type BillingPreferenceDetails = BillingPreferenceResponse & {
  recapFileType?: RecapFileType | null;
  recapFrequency?: RecapFrequency | null;
  statementFileType?: StatementFileType | null;
  applyFinancialCharges?: boolean | null;
};

interface PreferencesTabContentProps {
  clientData?: ClientGetByIdResponse;
  role: string;
  billingPreferencesData?: BillingPreferenceResponse;
  canViewBillingPreferences?: boolean;
}

const ClientDetailsContent: React.FC<PreferencesTabContentProps> = ({
  clientData,
  role,
  billingPreferencesData,
  canViewBillingPreferences = false,
}) => {
  const clientDetails = [
    { label: 'Phone', value: clientData?.phone ? formatPhoneNumber(clientData?.phone) : '-' },
    { label: 'Fax', value: clientData?.fax ?? '-' },
    { label: 'Website', value: clientData?.website ?? '-' },
  ].filter(Boolean);
  const billingPreferences = billingPreferencesData as BillingPreferenceDetails | undefined;
  const billingDetails = [
    ...(['ADMIN', 'ACCOUNTANT'].includes(role) || canViewBillingPreferences
      ? [
          {
            label: 'A/R Statements',
            value: billingPreferences?.isArRequired ? 'Yes' : 'No',
          },
          { label: 'Invoice Required', value: billingPreferences?.isInvoiceRequired ? 'Yes' : 'No' },
          { label: 'Recap Required', value: billingPreferences?.isRecapRequired ? 'Yes' : 'No' },
          {
            label: 'Recap File Type',
            value: RECAP_FILE_TYPE.find((type) => type.value === billingPreferences?.recapFileType)?.label ?? '-',
          },
          {
            label: 'Recap Frequency',
            value: RECAP_FREQUENCY.find((freq) => freq.value === billingPreferences?.recapFrequency)?.label ?? '-',
          },
          {
            label: 'Statement File Type',
            value:
              STATEMENT_FILE_TYPE.find((type) => type.value === billingPreferences?.statementFileType)?.label ?? '-',
          },
          {
            label: 'Apply Financial Charges',
            value: billingPreferences?.applyFinancialCharges ? 'Yes' : 'No',
          },
          {
            label: 'Billing Type',
            value: BILLING_TYPE.find((type) => type.value === (billingPreferences?.billingType as string | undefined))?.label ?? '-',
          },
          { label: 'Instructions', value: clientData?.instructions ?? '-' },
        ]
      : [{ label: 'Instructions', value: clientData?.instructions ?? '-' }]),
  ];
  return (
    <div className="flex flex-col gap-4 w-full px-6 py-4">
      <div className="flex flex-col gap-4 w-full">
        {clientDetails.map((contactDetails, index) => (
          <div key={index} className="flex flex-row justify-between items-start">
            <div className="text-[#969696] flex-shrink-0 w-1/3">{contactDetails?.label}</div>
            <div className="text-end font-normal w-2/3 break-words">{contactDetails?.value}</div>
          </div>
        ))}
        <hr />
        {billingDetails.map((preferencesDetails, index) => (
          <div key={index} className="flex flex-row justify-between items-start">
            <div className="font-medium flex-shrink-0 w-1/3">{preferencesDetails.label}</div>
            <div className="text-end font-normal w-2/3 break-words whitespace-pre-wrap">{preferencesDetails.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ClientDetailsContent;
